import { useMemo } from 'react'
import { useTheme, useMediaQuery } from '@material-ui/core'

export const DOTS = '...'

const range = (start, end) => {
  let length = end - start + 1
  return Array.from({ length }, (_, idx) => idx + start)
}

export const usePagination = ({
  totalCount,
  pageSize,
  siblingCount = 1,
  currentPage,
}) => {
  const theme = useTheme()
  // Use a media query to detect if the screen is "xs"
  const isXs = useMediaQuery(theme.breakpoints.down('xs'))

  const paginationRange = useMemo(() => {
    const totalPageCount = Math.ceil(totalCount / pageSize)

    if (isXs) {
      // For extra-small screens, we want to display only 3 pages.
      if (totalPageCount <= 3) return range(1, totalPageCount)
      // If on the first page, show pages [1,2,3]
      if (currentPage === 1) return [1, 2, 3]
      // If on the last page, show pages [last-2, last-1, last]
      if (currentPage === totalPageCount)
        return [totalPageCount - 2, totalPageCount - 1, totalPageCount]
      // Otherwise, show the previous, current, and next page
      return [currentPage - 1, currentPage, currentPage + 1]
    } else {
      // For larger screens, we use your original logic to show 5 items (including DOTS)
      const totalPageNumbers = siblingCount + 5

      // If the number of pages is less than the pagination component size, return all pages
      if (totalPageNumbers >= totalPageCount) {
        return range(1, totalPageCount)
      }

      const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
      const rightSiblingIndex = Math.min(
        currentPage + siblingCount,
        totalPageCount,
      )

      const shouldShowLeftDots = leftSiblingIndex > 2
      const shouldShowRightDots = rightSiblingIndex < totalPageCount - 2

      const firstPageIndex = 1
      const lastPageIndex = totalPageCount

      if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = 3 + 2 * siblingCount
        const leftRange = range(1, leftItemCount)
        return [...leftRange, DOTS, totalPageCount]
      }

      if (shouldShowLeftDots && !shouldShowRightDots) {
        const rightItemCount = 3 + 2 * siblingCount
        const rightRange = range(
          totalPageCount - rightItemCount + 1,
          totalPageCount,
        )
        return [firstPageIndex, DOTS, ...rightRange]
      }

      if (shouldShowLeftDots && shouldShowRightDots) {
        const middleRange = range(leftSiblingIndex, rightSiblingIndex)
        return [firstPageIndex, DOTS, ...middleRange, DOTS, lastPageIndex]
      }
    }
  }, [totalCount, pageSize, siblingCount, currentPage, isXs])

  return paginationRange
}
